import { useState } from 'react'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    LinearProgress,
    makeStyles,
    Divider,
    TextField
} from '@material-ui/core'

import CloseIcon from '@material-ui/icons/Close'
import MenuItem from '@material-ui/core/MenuItem'

import { useDataMutation, useBoolean, useNotification } from 'hooks'
import { logToAnalytics } from 'modules'

const useStyles = makeStyles(theme => ({
    titleWrapper: {
        paddingBottom: '0'
    },
    title: {
        paddingBottom: '16px',
        fontSize: '24px',
        fontWeight: theme.typography.fontWeightBold
    },
    closeButton: {
        position: 'absolute',
        right: 0,
        top: 0,
        padding: theme.spacing(0.5),
        margin: theme.spacing(0.5)
    },
    actions: {
        padding: theme.spacing(3),
        justifyContent: 'center',
        flexDirection: 'row'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        padding: `0 ${theme.spacing(6)}`,
        maxHeight: '520px',
        overflowY: 'auto',
        '& > *': {
            marginBottom: theme.spacing(3)
        }
    },
    fieldLabel: {
        color: '#7f8c9a'
    }
}))

export const REQUEST_TYPES = [
    { value: 'question', label: 'Support Question' },
    { value: 'bug', label: 'Report a Problem' },
    { value: 'feature_request', label: 'New Feature' },
    { value: 'config_change', label: 'Configuration Change' },
    { value: 'feedback', label: 'Feedback' }
]

const initialType = 'feedback'

export const FeedbackDialog = ({ isOpen, close }) => {
    const classes = useStyles()

    const loading = useBoolean()
    const { showSuccessNotification, showErrorNotification } = useNotification()

    const [subject, setSubject] = useState('')
    const [requestType, setRequestType] = useState(initialType)

    const $feedback = useDataMutation('/b/v1/userFeedback', 'POST')

    const sendFeedback = () => {
        const payload = {
            subject: subject.trim(),
            requestType,
            originalURL: window.location.href
        }

        $feedback
            .mutate(payload)
            .then(() => {
                logToAnalytics('user_feedback_sent', payload)
                showSuccessNotification('Thank you! Your feedback was successfully sent.')
            })
            .catch(() => {
                showErrorNotification('Something went wrong')
            })
            .finally(() => {
                loading.setFalse()
                close()
                setSubject('')
                setRequestType(initialType)
            })
    }

    return (
        <Dialog open={isOpen} fullWidth onClose={close} maxWidth="md">
            {loading.isTrue && <LinearProgress />}
            <IconButton className={classes.closeButton} onClick={close}>
                <CloseIcon />
            </IconButton>

            <DialogTitle classes={{ root: classes.titleWrapper }}>
                <Typography className={classes.title}>Give feedback to Guidde</Typography>
                <Divider />
            </DialogTitle>
            <DialogContent className={classes.content}>
                <TextField
                    fullWidth
                    color="primary"
                    value={subject}
                    focused
                    onChange={e => setSubject(e.target.value)}
                    label="Subject"
                    InputLabelProps={{
                        shrink: true,
                        required: true,
                        classes: { root: classes.fieldLabel }
                    }}
                />
                <TextField
                    select
                    fullWidth
                    value={requestType}
                    color="primary"
                    onChange={e => setRequestType(e.target.value)}
                    label="Request Type"
                    InputLabelProps={{
                        shrink: true,
                        classes: { root: classes.fieldLabel }
                    }}
                >
                    {REQUEST_TYPES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    size="medium"
                    type="submit"
                    disabled={loading.isTrue || !subject.length}
                    onClick={sendFeedback}
                >
                    Send feedback
                </Button>
            </DialogActions>
        </Dialog>
    )
}
